// DemandPreviewDrawer.jsx
// Modal/drawer showing stock position, upcoming jobs, PO coverage, and vendor options

import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PurchasingAPI from '../../../services/purchasingService';
import StockStatusBadge, { getStockStatus } from './StockStatusBadge';
import { convertFromUTC } from '../../../util/convert_timezone';

class DemandPreviewDrawer extends Component {
    state = {
        data: null,
        loading: true,
        error: null
    };

    async componentDidMount() {
        await this.loadDemand();
    }

    loadDemand = async () => {
        try {
            const data = await PurchasingAPI.getDemandPreview(this.props.productId, this.props.installerId, this.props.locationId);
            this.setState({ data, loading: false });
        } catch (err) {
            console.error('Error loading demand preview:', err);
            this.setState({ error: 'Failed to load demand data.', loading: false });
        }
    };

    render() {
        const { show, onHide, productName } = this.props;
        const { data, loading, error } = this.state;

        return (
            <Modal show={show} onHide={onHide} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa-sharp fa-regular fa-chart-line mr-2"></i>
                        Demand Preview — {productName || 'Product'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <style>{`
                        .demand-section { margin-bottom: 24px; }
                        .demand-section h6 {
                            font-size: 13px;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            color: #475569;
                            font-weight: 600;
                            margin-bottom: 12px;
                        }
                        .stock-grid {
                            display: grid;
                            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
                            gap: 12px;
                            margin-bottom: 16px;
                        }
                        .stock-item {
                            text-align: center;
                            padding: 12px;
                            background: #f8fafc;
                            border-radius: 6px;
                            border: 1px solid #e5e7eb;
                        }
                        .stock-item .number { font-size: 20px; font-weight: 700; color: #374151; }
                        .stock-item .label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
                        .stock-item.projected { border-color: #3FB2BE; background: #f0fdf4; }
                        .stock-bar {
                            height: 24px;
                            border-radius: 4px;
                            background: #f3f4f6;
                            position: relative;
                            overflow: hidden;
                            margin-bottom: 16px;
                        }
                        .stock-bar-segment {
                            height: 100%;
                            display: inline-block;
                            position: absolute;
                            top: 0;
                        }
                        .demand-table {
                            width: 100%;
                            border-collapse: collapse;
                            font-size: 13px;
                        }
                        .demand-table th {
                            background: #f1f5f9;
                            padding: 8px 10px;
                            font-weight: 600;
                            color: #475569;
                            text-align: left;
                            border-bottom: 1px solid #e2e8f0;
                        }
                        .demand-table td {
                            padding: 8px 10px;
                            border-bottom: 1px solid #f3f4f6;
                            color: #374151;
                        }
                    `}</style>

                    {loading ? (
                        <div className="text-center py-4">
                            <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                            <p className="mt-3">Loading demand data...</p>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : data ? (
                        <>
                            {/* Stock Position */}
                            <div className="demand-section">
                                <h6>Stock Position</h6>
                                <div className="stock-grid">
                                    <div className="stock-item">
                                        <div className="number">{data.on_hand ?? 0}</div>
                                        <div className="label">On Hand</div>
                                    </div>
                                    <div className="stock-item">
                                        <div className="number" style={{ color: '#3b82f6' }}>{data.incoming ?? 0}</div>
                                        <div className="label">Incoming</div>
                                    </div>
                                    <div className="stock-item">
                                        <div className="number" style={{ color: '#dc2626' }}>{data.committed ?? 0}</div>
                                        <div className="label">Committed</div>
                                    </div>
                                    <div className="stock-item projected">
                                        <div className="number">{data.projected ?? ((data.on_hand || 0) + (data.incoming || 0) - (data.committed || 0))}</div>
                                        <div className="label">Projected</div>
                                    </div>
                                    <div className="stock-item">
                                        <div className="number" style={{ color: '#6b7280' }}>{data.reorder_point ?? 0}</div>
                                        <div className="label">Reorder Level</div>
                                    </div>
                                </div>
                                <div>
                                    <StockStatusBadge status={getStockStatus(
                                        data.projected ?? ((data.on_hand || 0) + (data.incoming || 0) - (data.committed || 0)),
                                        data.reorder_point || 0
                                    )} />
                                </div>
                            </div>

                            {/* Upcoming Jobs */}
                            {data.upcoming_jobs && data.upcoming_jobs.length > 0 && (
                                <div className="demand-section">
                                    <h6>Upcoming Jobs Requiring This Product</h6>
                                    <table className="demand-table">
                                        <thead>
                                            <tr>
                                                <th>Job</th>
                                                <th>Customer</th>
                                                <th>Vehicle</th>
                                                <th>Scheduled</th>
                                                <th>Qty</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.upcoming_jobs.map((job, i) => (
                                                <tr key={i}>
                                                    <td>{job.request_id || '—'}</td>
                                                    <td>{job.customer_name || '—'}</td>
                                                    <td>{job.vehicle || '—'}</td>
                                                    <td>{job.scheduled_date ? convertFromUTC(job.scheduled_date, null, 'MM/DD') : '—'}</td>
                                                    <td>{job.quantity || 1}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Open PO Coverage */}
                            {data.open_pos && data.open_pos.length > 0 && (
                                <div className="demand-section">
                                    <h6>Open PO Coverage</h6>
                                    <table className="demand-table">
                                        <thead>
                                            <tr>
                                                <th>PO #</th>
                                                <th>Vendor</th>
                                                <th>Ordered</th>
                                                <th>Received</th>
                                                <th>Expected</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.open_pos.map((po, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <Link to={`/purchase-orders/${po.purchase_order_id}`} style={{ color: '#3FB2BE' }}>
                                                            {po.po_number || `PO-${po.purchase_order_id}`}
                                                        </Link>
                                                    </td>
                                                    <td>{po.vendor_name || '—'}</td>
                                                    <td>{po.quantity_ordered}</td>
                                                    <td>{po.quantity_received || 0}</td>
                                                    <td>{po.expected_date ? convertFromUTC(po.expected_date, null, 'MM/DD') : '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Vendor Options */}
                            {data.vendors && data.vendors.length > 0 && (
                                <div className="demand-section">
                                    <h6>Vendor Options</h6>
                                    <table className="demand-table">
                                        <thead>
                                            <tr>
                                                <th>Vendor</th>
                                                <th>SKU</th>
                                                <th>Unit Cost</th>
                                                <th>Lead Time</th>
                                                <th>Preferred</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.vendors.map((v, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <Link to={`/vendors/${v.vendor_id}`} style={{ color: '#3FB2BE' }}>
                                                            {v.vendor_name}
                                                        </Link>
                                                    </td>
                                                    <td>{v.vendor_sku || '—'}</td>
                                                    <td>{v.unit_cost ? `$${parseFloat(v.unit_cost).toFixed(2)}` : '—'}</td>
                                                    <td>{v.lead_time_days ? `${v.lead_time_days}d` : '—'}</td>
                                                    <td>{v.preferred ? <i className="fa-sharp fa-solid fa-star" style={{ color: '#f59e0b' }}></i> : '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Empty states */}
                            {(!data.upcoming_jobs || data.upcoming_jobs.length === 0) &&
                             (!data.open_pos || data.open_pos.length === 0) &&
                             (!data.vendors || data.vendors.length === 0) && (
                                <div className="text-center py-3 text-muted">
                                    <p>No additional demand data available.</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-3 text-muted">
                            <p>No demand data available.</p>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        );
    }
}

export default DemandPreviewDrawer;
